import { type ReactNode } from "react";
import { StatusBadge } from "../../components/index.ts";
import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import { DrawerSection, KeyValue } from "./DetailDrawer.tsx";
import { ReviewPill, statusLevel } from "./components.tsx";
import type { Intent } from "./types.ts";

/**
 * The detail shown in the right-side drawer for one intent (modelled on the AgentDNA dashboard's
 * IntentDetail): a header with the name, id and review state, an Overview (initiator, timing, the
 * agents/apps it touched, threats), and a Reliability score with a bar. Pure presentation over the
 * already-fetched row.
 */
export function IntentDetail(props: {
  readonly intent: Intent;
  readonly onClose: () => void;
  readonly titleId: string;
}): ReactNode {
  const i = props.intent;
  return (
    <>
      <header className="drawer-head">
        <div className="drawer-head__title">
          <h2 id={props.titleId} className="drawer-head__heading">
            {i.name}
          </h2>
          <span className="drawer-head__id">{i.id}</span>
        </div>
        <ReviewPill status={i.reviewStatus} />
        <button type="button" className="drawer-head__close" onClick={props.onClose} aria-label="Close details">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>

      <div className="drawer-body">
        <DrawerSection title="Overview">
          <KeyValue
            rows={[
              { k: "Intent ID", v: <code className="drawer-mono">{i.id}</code> },
              { k: "Initiator", v: i.initiator.name },
              { k: "Status", v: <StatusBadge level={statusLevel(i.status)} size="sm" /> },
              { k: "Review", v: <ReviewPill status={i.reviewStatus} /> },
              {
                k: "Started",
                v: <time dateTime={isoAttr(i.started)}>{formatDateTime(i.started)}</time>,
              },
              { k: "Runtime", v: `${i.runtime} ms` },
              { k: "Interactions", v: i.interactionsCount },
              { k: "Agents touched", v: i.agentsInteracted },
              { k: "Apps touched", v: i.toolsInteracted },
              {
                k: "Threats",
                v: <span className={i.threats > 0 ? "drawer-threat" : "drawer-safe"}>{i.threats}</span>,
              },
              ...(i.provenanceRecordID !== ""
                ? [{ k: "Provenance", v: <code className="drawer-mono">{i.provenanceRecordID}</code> }]
                : []),
            ]}
          />
        </DrawerSection>

        <DrawerSection title="Reliability">
          <div className="drawer-score">
            <span className="drawer-score__value">{i.score}</span>
            <div className="drawer-score__bar" role="img" aria-label={`Reliability score ${i.score} of 100`}>
              <span className="drawer-score__fill" style={{ width: `${Math.max(0, Math.min(100, i.score))}%` }} />
            </div>
          </div>
          <p className="drawer-score__note">
            Based on successful sub-task completion and policy adherence.
          </p>
        </DrawerSection>
      </div>
    </>
  );
}
